/**
 * CatalogoCursos - Página con el catálogo completo de cursos
 * Muestra todos los cursos disponibles del Instituto Valparaíso
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    Clock,
    Laptop,
    MapPin,
    Award,
    ArrowRight,
    Sparkles,
    Shield,
    Users,
    Flame,
    Camera,
    AlertCircle,
    GraduationCap,
    UserPlus,
    Zap,
    RefreshCw
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InterestForm from '../components/InterestForm';

// IDs de cursos presenciales con cupo mínimo (deben coincidir con la configuración del backend)
const PRESENTIAL_COURSE_IDS = {
    'bastonEsposas': 101,
    'defensaPersonal': 102,
    'extintores': 103
};

// Todos los cursos disponibles
const allCourses = [
    {
        title: 'Formación Guardia de Seguridad Privada',
        description: 'Formación integral en prevención de riesgos y salud ocupacional. Certificación oficial reconocida por organismos competentes.',
        duration: '90 horas',
        modality: 'Presencial + Online',
        price: '$110.000',
        image: '/institu1-formacion.webp',
        accent: 'from-blue-500 to-cyan-500',
        link: '/curso/guardia-os10',
        icon: Shield,
    },
    {
        title: 'Perfeccionamiento Guardia de Seguridad',
        description: 'Actualiza tus competencias como guardia de seguridad privada. 36 horas presenciales con certificación SPD para renovar tu acreditación.',
        duration: '36 horas',
        modality: 'Presencial + Online',
        price: '$90.000',
        image: '/institu2-perfec.webp',
        accent: 'from-indigo-500 to-blue-500',
        link: '/curso/perfeccionamiento-guardia',
        icon: RefreshCw,
        presentialMinQuota: true,
        courseId: 104,
    },
    {
        title: 'Especialización Guardia de Seguridad',
        description: 'Especialízate en seguridad para entidades de riesgo medio y alto. Habilítate para rendir examen ante la autoridad fiscalizadora.',
        duration: '36 horas',
        modality: 'Presencial + Online',
        price: '$95.000',
        image: '/instituteroom.webp',
        accent: 'from-purple-500 to-violet-500',
        link: '/curso/especializacion-guardia',
        icon: GraduationCap,
        presentialMinQuota: true,
        courseId: 105,
    },
    {
        title: 'Supervisor de Seguridad Privada',
        description: 'Capacitación completa para liderar equipos de seguridad. Técnicas de supervisión, control y gestión de personal.',
        duration: '140 horas',
        modality: '100% Online',
        price: '$100.000',
        image: '/institu2-supr.webp',
        accent: 'from-violet-500 to-purple-500',
        link: '/curso/supervisor-seguridad',
        icon: Users,
        popular: true,
    },
    {
        title: 'Bastón y Esposas',
        description: 'Técnicas profesionales para el uso correcto de bastón de seguridad y esposas. Entrenamiento práctico y normativa legal.',
        duration: '5 horas',
        modality: 'Presencial',
        price: '$30.000',
        priceNumber: 30000,
        image: '/bsyesposas.webp',
        accent: 'from-orange-500 to-red-500',
        link: '#',
        icon: Shield,
        presentialMinQuota: true,
        courseId: PRESENTIAL_COURSE_IDS.bastonEsposas,
    },
    {
        title: 'Defensa Personal y Técnicas de Reducción',
        description: 'Aprende técnicas efectivas de defensa personal y reducción de agresores. Entrenamiento práctico con instructores certificados.',
        duration: '10 horas',
        modality: 'Presencial',
        price: '$70.000',
        priceNumber: 70000,
        image: '/dojo_instituto.webp',
        accent: 'from-red-500 to-pink-500',
        link: '#',
        icon: Shield,
        presentialMinQuota: true,
        courseId: PRESENTIAL_COURSE_IDS.defensaPersonal,
    },
    {
        title: 'Uso y Manejo de Extintores',
        description: 'Capacitación práctica en el uso correcto de extintores. Tipos de fuego, técnicas de extinción y prevención de incendios.',
        duration: '5 horas',
        modality: 'Presencial',
        price: '$30.000',
        priceNumber: 30000,
        image: '/exti.webp',
        accent: 'from-amber-500 to-orange-500',
        link: '#',
        icon: Flame,
        presentialMinQuota: true,
        courseId: PRESENTIAL_COURSE_IDS.extintores,
    },
    {
        title: 'Operador CCTV',
        description: 'Formación completa en operación de sistemas de videovigilancia. Monitoreo, análisis de imágenes y protocolos de seguridad.',
        duration: '50 horas',
        modality: '100% Online',
        price: '$90.000',
        image: '/cctv.webp',
        accent: 'from-cyan-500 to-blue-500',
        link: '#',
        icon: Camera,
    },
    {
        title: 'Electricidad Básica e Instalación Domiciliaria',
        description: 'Aprende los fundamentos de electricidad e instalaciones domiciliarias.',
        duration: '7 días',
        modality: 'Presencial',
        price: '$75.000',
        image: '/cursoelectricidad.webp',
        accent: 'from-amber-500 to-yellow-500',
        link: '/curso/electricidad-basica',
        icon: Zap,
        presentialMinQuota: true,
        courseId: 106,
    },
];

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function CatalogoCursos() {
    // State for interest form modal
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [isInterestFormOpen, setIsInterestFormOpen] = useState(false);

    const handleOpenInterestForm = (course) => {
        setSelectedCourse(course);
        setIsInterestFormOpen(true);
    };

    const handleCloseInterestForm = () => {
        setIsInterestFormOpen(false);
        setSelectedCourse(null);
    };

    return (
        <div className="min-h-screen bg-slate-900">
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                {/* Background decorations */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-500/5 to-cyan-500/5 rounded-full blur-3xl" />

                    {/* Grid pattern */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]" />
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="inline-block px-4 py-1.5 bg-blue-500/20 text-blue-400 text-sm font-medium rounded-full mb-6">
                            Catálogo Completo
                        </span>

                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading">
                            Todos Nuestros{' '}
                            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                                Cursos
                            </span>
                        </h1>

                        <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                            Descubre nuestra oferta completa de programas de capacitación profesional.
                            Cursos presenciales y online con certificación reconocida.
                        </p>

                        <div className="flex flex-wrap justify-center gap-6 text-slate-400">
                            <div className="flex items-center gap-2">
                                <GraduationCap className="w-5 h-5 text-blue-400" />
                                <span>9 Cursos Disponibles</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Award className="w-5 h-5 text-cyan-400" />
                                <span>Certificación OTEC</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Courses Grid */}
            <section className="py-20 bg-slate-900 relative">
                <div className="container mx-auto px-4">
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        {allCourses.map((course, index) => {
                            const Icon = course.icon;
                            const isClickable = course.link !== '#';

                            const CardContent = (
                                <motion.div
                                    variants={itemVariants}
                                    whileHover={isClickable ? { y: -10, scale: 1.02 } : {}}
                                    className={`group bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300 flex flex-col h-full relative ${!isClickable ? 'cursor-default' : 'cursor-pointer'}`}
                                >
                                    {/* Badges */}
                                    <div className="absolute top-4 right-4 z-20 flex flex-col gap-2">
                                        {course.popular && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                            >
                                                <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-orange-500/30">
                                                    <Sparkles className="w-3 h-3" />
                                                    Popular
                                                </div>
                                            </motion.div>
                                        )}
                                        {course.unavailable && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                            >
                                                <div className="flex items-center gap-1.5 bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-slate-500/30">
                                                    No disponible
                                                </div>
                                            </motion.div>
                                        )}
                                    </div>

                                    {/* Image container */}
                                    <div className="relative h-52 overflow-hidden">
                                        <div className={`absolute inset-0 bg-gradient-to-t ${course.accent} opacity-0 group-hover:opacity-30 transition-opacity duration-500 z-10`} />
                                        <img
                                            src={course.image}
                                            alt={course.title}
                                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                        />
                                        {/* Bottom gradient */}
                                        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-900 to-transparent z-10" />
                                    </div>

                                    {/* Content */}
                                    <div className="p-6 flex flex-col flex-grow">
                                        <div className="flex items-start gap-3 mb-3">
                                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${course.accent} flex items-center justify-center flex-shrink-0`}>
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors flex-grow">
                                                {course.title}
                                            </h3>
                                        </div>

                                        <p className="text-slate-400 text-sm mb-6 flex-grow leading-relaxed">
                                            {course.description}
                                        </p>

                                        {/* Meta info */}
                                        <div className="flex flex-wrap gap-4 mb-4">
                                            <div className="flex items-center gap-2 text-sm text-slate-400">
                                                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                                                    <Clock className="w-4 h-4 text-blue-400" />
                                                </div>
                                                <span>{course.duration}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-slate-400">
                                                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                                                    {course.modality.includes('Online') ? (
                                                        <Laptop className="w-4 h-4 text-cyan-400" />
                                                    ) : (
                                                        <MapPin className="w-4 h-4 text-emerald-400" />
                                                    )}
                                                </div>
                                                <span>{course.modality}</span>
                                            </div>
                                        </div>

                                        {/* Price */}
                                        <div className="mb-4">
                                            <p className="text-2xl font-bold text-white">
                                                {course.price} <span className="text-sm text-slate-400">CLP</span>
                                            </p>
                                        </div>

                                        {/* CTA Button */}
                                        {isClickable ? (
                                            <motion.div
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                className={`w-full bg-gradient-to-r ${course.accent} text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg group/btn`}
                                            >
                                                Más Información
                                                <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                                            </motion.div>
                                        ) : course.presentialMinQuota ? (
                                            <motion.button
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleOpenInterestForm(course);
                                                }}
                                                className={`w-full bg-gradient-to-r ${course.accent} text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg group/btn cursor-pointer`}
                                            >
                                                <UserPlus className="w-4 h-4" />
                                                Registrar Interés
                                            </motion.button>
                                        ) : (
                                            <div className="w-full bg-white/10 text-slate-400 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 border border-white/10">
                                                <AlertCircle className="w-4 h-4" />
                                                Próximamente
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            );

                            return isClickable ? (
                                <Link key={index} to={course.link}>
                                    {CardContent}
                                </Link>
                            ) : (
                                <div key={index}>
                                    {CardContent}
                                </div>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <GraduationCap className="w-16 h-16 text-white/80 mx-auto mb-6" />
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-heading">
                            ¿Listo para Comenzar?
                        </h2>
                        <p className="text-xl text-blue-100 mb-8">
                            Elige el curso que mejor se adapte a tus necesidades y comienza tu formación profesional hoy mismo.
                        </p>

                        <motion.button
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            className="inline-flex items-center gap-2 px-10 py-5 bg-white text-blue-600 font-bold rounded-xl shadow-xl hover:bg-blue-50 transition-all text-lg"
                        >
                            Ver Cursos Disponibles
                            <ArrowRight className="w-6 h-6" />
                        </motion.button>

                        <div className="flex flex-wrap justify-center gap-6 mt-10 text-blue-100">
                            <div className="flex items-center gap-2">
                                <Award className="w-5 h-5" />
                                <span>Certificación OTEC</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                <span>Instructores certificados</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Shield className="w-5 h-5" />
                                <span>Más de 500 egresados</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />

            {/* Interest Form Modal for presential courses */}
            {selectedCourse && (
                <InterestForm
                    courseId={selectedCourse.courseId}
                    courseName={selectedCourse.title}
                    isOpen={isInterestFormOpen}
                    onClose={handleCloseInterestForm}
                    onSuccess={() => {
                        // Don't close immediately - let user see success message
                        // User can close manually with the "Cerrar" button
                    }}
                />
            )}
        </div>
    );
}
